import type { PlatformType } from '@/types'

import { AdServiceTargetingOptions } from './AdServiceTargetingOptions'
import { Banner } from './banner'
import type { OutOfPageBanner } from './out-of-page-banner'

export type BannerEventType = 'closed' | 'rewarded' | 'empty' | 'ready' | 'rendered' | 'viewable' | 'timer' | 'clicked'

export type BannerEventListenerArgs = [
    type: BannerEventType,
    callback: (event: Event & { detail?: { secondsLeft?: number } }) => void,
]

export type PageAdType =
    | 'sidebar'
    | 'sticky_desktop'
    | 'sticky_portrait'
    | 'sticky_mobile'
    | 'sticky_tablet'
    | 'leaderboard'
    | 'leaderboard_rest'
    | 'catalog_mobile'
    // widget page ad slots
    | 'widget_sidebar'
    | 'widget_sidebar_bottom'
    | 'widget_horizontal'
    | 'widget_horizontal_2'
    | 'widget_sticky_mobile'
    | 'widget_sticky_tablet'

export type OutOfPageAdType =
    | 'fullscreen'
    | 'interstitial'
    | 'interstitial_preroll'
    | 'interstitial_preroll_2'
    | 'rewarded'
    | 'rewarded_2'
    | 'sticky_portrait' // might be static and out-of-page

export enum AdProviderStatus {
    loading = 'loading',
    online = 'online',
    offline = 'offline',
}

export type AdUnitConfig = Record<'desktop' | 'mobile', Partial<Record<OutOfPageAdType | PageAdType, string>>>

export type AdUnitProvidersConfig<T extends string> = Record<T, AdUnitConfig>

export enum AdvAction {
    preloadRewarded = 'preloadRewarded',
    showRewarded = 'showRewarded',
    preloadInterstitial = 'preloadInterstitial',
    showInterstitial = 'showInterstitial',
    setBannerAdvSettings = 'setBannerAdvSettings',
}

export type PageAdSize = 'fluid' | [number, number]

export type PageAdOptions = {
    el?: string // id of element where to mount the ad
    sizes?: PageAdSize[]
    type: PageAdType
    refresh?: number // seconds
    clid?: string
    gameId?: string
    userId?: string
}

export type AdProviderConfig = {
    targeting?: AdServiceTargetingOptions
    platform: PlatformType
    auid: string
    isWidget: boolean
    adUnitConfig?: AdUnitConfig
    useOnlyConfig?: boolean
}

export abstract class AdProvider {
    protected constructor(_: AdProviderConfig) {}

    abstract readonly serviceStatus: Promise<AdProviderStatus>

    abstract prepareOutOfPageAd(type: OutOfPageAdType, timeout?: number): Promise<OutOfPageBanner>

    abstract requestPageAd(options: PageAdOptions): Promise<Banner>

    abstract updateTargeting(targeting: AdProviderConfig['targeting']): void

    abstract getAdUnitByType(type: OutOfPageAdType | PageAdType): string | undefined
}

export class NotImplementedError extends Error {
    constructor(message = 'Not implemented yet') {
        super(message)
        this.name = 'NotImplementedError'
    }
}
